import {
  Box,
  Typography,
  makeStyles,
  styled,
  useMediaQuery,
} from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import { Link } from "@mui/material";
import { useDispatch, useSelector } from "src/store";
import MuiButton from "@mui/material/Button";
import { monthTicks } from "src/mock/graph";
import {
  setAddressInStore,
  setShowBaseGraphButtonInStore,
  setShowRateGraphInStore,
  setUsageInStore,
  setViewInStore,
} from "src/slices/bkvReducer";
import GimmickyIcon from "src/assets/savings_gimmicky.svg";
import BlueBonnetIcon from "src/assets/savings_bluebonnet.svg";
import SavingsIcon from "src/assets/savings_icon.svg";
import SavingsGraph from "./widgets/SavingsGraph";
import { useTheme } from "@mui/material/styles";
import { AddressField } from "./form/InputFields";
import SimpleText from "./SimpleText";

export const ComparisonChart = ({ plan1, plan2, handleOpen }) => {
  const state = useSelector((state) => state.bkv);
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery("(max-width:500px)");
  const [search, setSearch] = useState("");
  const [dim, setDim] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [csavingsPercentage, setCsavingsPercentage] = useState(0);
  const [csavings, setCsavings] = useState(0);
  const [loading, setLoading] = useState(true);

  const [graphData, setGraphData] = useState([]);
  const savingPercentage = 15;
  const lg = dim.width > 960;
  const md = dim.width < 960 && dim.width > 600;
  const sm = dim.width < 600;

  // const [buttonHeight, setButtonHeight] = useState("12rem")
  const ctaRef = useRef();
  const Button = styled(MuiButton)((props) => ({
    borderRadius: "10vw",
    whiteSpace: "nowrap",
  }));

  useEffect(() => {
    if (!plan1 || !plan2) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [plan1, plan2]);

  useEffect(() => {
    function handleWindowResize() {
      const { innerWidth, innerHeight } = window;
      let winSize = { width: innerWidth, height: innerHeight };
      setDim(winSize);
    }
    handleWindowResize();
    window.addEventListener("resize", handleWindowResize);
    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  useEffect(() => {
    if (!plan1 || !plan2 || Object.keys(state.usage).length === 0) return;
    const costData = [];
    const usageArr = Object.values(state.usage) as number[];
    for (let index = 0; index < 12; index++) {
      const usage = usageArr[index];
      let temp = 2500;
      [2000, 1000, 500].map((item) => {
        if (usage < temp) {
          temp = item;
        }
        if (temp > 2000) {
          temp = 2000;
        }
      });
      const gim = parseInt(Number(plan2.rates[`${temp}kWh`]).toFixed(1)) / 100;
      const bb =
        parseInt(
          Number(plan1.rates[`${temp}kWh`].replace("¢", "")).toFixed(1)
        ) / 100;
      // console.log(gim, bb);
      costData.push({
        name: monthTicks[index],
        gimmicky: parseInt((gim * usage).toFixed(0)),
        bluebonnet: parseInt((bb * usage).toFixed(0)),
      });
    }

    const gimmickysum = costData.reduce((accumulator, object) => {
      return accumulator + object.gimmicky;
    }, 0);

    const bluebonnetsum = costData.reduce((accumulator, object) => {
      return accumulator + object.bluebonnet;
    }, 0);

    const cumulativeSavingsPercentage =
      ((gimmickysum - bluebonnetsum) / gimmickysum) * 100;
    setCsavingsPercentage(cumulativeSavingsPercentage);
    setCsavings(gimmickysum - bluebonnetsum);
    setGraphData(costData);
    // console.log("costData", costData);
  }, [plan1, plan2, state.usage]);

  const handleSubmit = () => {
    handleOpen("usageModal");
    dispatch(setViewInStore("showUsageGraph"));
    dispatch(setUsageInStore({}));
    // dispatch(setShowBaseGraphButtonInStore(true));
    // dispatch(setShowRateGraphInStore(true));
    return;
  };

  const CustomH1 = styled("h1")({
    color: "#09507F",
    fontFamily: "Larken DEMO medium",
    fontSize: "36px",
    fontWeight: 400,
    lineHeight: "47.7px",
    textAlign: "left",
    marginTop: "28px",
    marginBottom: "28px",
    "@media (max-width: 768px)": {
      fontSize: "25px",
      lineHeight: "35px",
      fontWeight: 600,
    },
  });

  return (
    <>
      <CustomH1>Compare an estimated annual savings.</CustomH1>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          padding: {
            xs: "2% 0% 2% 0%",
            sm: "2% 0% 2% 0%",
            md: "2% 3% 2% 3%",
          },
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            whiteSpace: { xs: "wrap", sm: "wrap", md: "wrap", lg: "nowrap" },
            boxShadow: "0px 0px 15px 0px rgba(0, 0, 0, 0.15)",
            borderRadius: { xs: "5vw", sm: "5vw", md: "5vw", lg: "20px" },
            position: "relative",
            boxSizing: "border-box",
            padding: {
              xs: "5% 8% 5% 8%",
              sm: "5% 8% 5% 8%",
              md: "5% 10% 5% 10%",
              lg: "20px 166px",
            },
            display: "flex",
            alignItems: "center",
            width: "100%",
            flexDirection: "column",
          }}
        >
          <SimpleText
            style={{
              fontSize: {
                xs: "13px",
                md: "3.5vw",
                lg: "20px",
              },
              textAlign: "center",
              marginBottom: { xs: "5%", sm: "5%", mb: "5%", lg: "0" },
              color: "#2f2f2f",
            }}
            text={""}
          />
          <Box
            width="100%"
            sx={{
              display: "flex",
              justifyContent: "center",
              flexDirection: {
                lg: "row",
                md: "row",
                sm: "column",
                xs: "column",
              },
              marginTop: "2%",
              gap: {
                xs: "12px",
                md: "28px",
              },
            }}
          >
            <AddressField
              width={100}
              value={search}
              setValue={setSearch}
              placeholder={"ENTER SERVICE ADDRESS.."}
              defaultValue={state.address}
              takeMargin={false}
              handleSelect={(address) => dispatch(setAddressInStore(address))}
            />
            <Button
              disabled={!state.address || state.elementLoading["address"]}
              className={classes.button}
              sx={{
                padding: {
                  xs: "3% 15%",
                  sm: "3% 15%",
                  md: "2% 15%",
                  lg: "13px 28px",
                },
                minWidth: "190px",
                backgroundColor: loading
                  ? theme.palette.action.disabled
                  : theme.palette.secondary.main,
                "&:hover": {
                  backgroundColor: (theme) => theme.palette.secondary.dark,
                },
              }}
              onClick={handleSubmit}
            >
              <Typography
                fontSize={{ xs: "12px", md: "3vw", lg: "15px" }}
                sx={{ lineHeight: "21px", fontWeight: "1000" }}
              >
                Personalize
              </Typography>
            </Button>
          </Box>
        </Box>
      </Box>
      {Object.keys(state.usage).length !== 0 && (
        <div>
          {/* <Typography
            sx={{
              fontSize: { xs: "5.5vw", sm: "5.5vw", md: "5vw", lg: "60px" },
              color: theme.palette.primary.main,
              whiteSpace: "wrap",
              marginTop: { xs: "10%", sm: "10%", md: "6%", lg: "4%" },
              marginBottom: { xs: "10%", sm: "10%", md: "6%", lg: "2%" },
            }}
            align="center"
            variant="body2"
            padding={{ xs: "0 10%", sm: "0 10%", md: "0 10%", lg: "0 5%" }}
          >
            {!isNaN(csavings) && csavings <= 0
              ? "You're one of the few Texans with the right energy usage who may save money by sticking with the Polyedra plan"
              : `Last year you would have saved $${
                  isNaN(csavings) ? 0 : csavings
                } with Bluebonnet`}
          </Typography> */}
          <Box className={classes.root}>
            <Box id="savings-graph" className={classes.graphContainer}>
              <Box className={classes.legend}>
                <Box className="container">
                  <img src={GimmickyIcon} alt="gimmicky-icon" />
                  <Typography>{plan2.name}</Typography>
                </Box>
                <Box className="container">
                  <img src={BlueBonnetIcon} alt="bluebonnet-icon" />
                  <Typography>Bluebonnet plan</Typography>
                </Box>
                {!sm && (
                  <Box className="container">
                    <img src={SavingsIcon} alt="savings-icon" />
                    <Typography>Cumulative Savings</Typography>
                  </Box>
                )}
              </Box>
              <SavingsGraph data={graphData} dataKey={"name"} />
            </Box>
          </Box>
        </div>
      )}
    </>
  );
};

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    justifyContent: "center",
    padding: "1% 3% 1% 3%",
    [theme.breakpoints.down("md")]: {
      padding: "1% 5% 1% 5%",
    },
  },
  title: {
    fontFamily: "Larken DEMO medium",
    color: "#09507F",
    textAlign: "center",
  },
  graphContainer: {
    marginTop: 30,
    borderRadius: 40,
    border: "1px solid #99D7CF",
    padding: "20px 15px 20px 15px",
    width: "100%",
    [theme.breakpoints.down("sm")]: {
      borderRadius: 16,
      marginTop: 0,
    },
  },
  legend: {
    margin: "10px auto 3% 2%",
    display: "flex",
    gap: 60,
    "& .container": {
      display: "flex",
      gap: 8,
      alignItems: "center",
    },
    [theme.breakpoints.down("sm")]: {
      gap: 30,
      "& img": {
        width: 30,
        height: 5,
        objectFit: "cover",
      },
      "& p": {
        fontSize: 10,
      },
    },
  },
  belowGraph: {
    boxSizing: "border-box",
    marginTop: 40,
    gap: 30,
    padding: "10px 10px",
    [theme.breakpoints.down("sm")]: {
      marginTop: 20,
    },
    [theme.breakpoints.down("sm")]: {
      marginTop: 20,
      padding: 0,
    },
  },
  premise: {
    boxSizing: "border-box",
    flex: 0.5,
    boxShadow: "0px 0px 15px 0px rgba(0, 0, 0, 0.15)",
    padding: "30px 20px",
    display: "flex",
    borderRadius: 15,
    alignItems: "start",
    height: "12rem",
  },
  cta: {
    height: "12rem",
    boxSizing: "border-box",
    flex: 0.5,
    borderRadius: 15,
    color: "#fff",

    background:
      "linear-gradient(94deg, #FFD27D -4.14%, #00B49D 36.64%, #09507F 79.16%)",
    textAlign: "center",
    padding: 20,
    display: "flex",
    justifyContent: "center",
    "& .inner": {
      width: "100%",
    },
    [theme.breakpoints.down("lg")]: {
      height: "20%",
    },
  },
  premiseDetails: {
    alignSelf: "center",
    display: "flex",
    width: "100%",
    alignItems: "center",
    flexDirection: "column",
    "& .street": {
      fontWeight: "bold",
    },
    "& .details": {
      marginTop: 10,
      display: "flex",
      alignItems: "center",
      textAlign: "center",
      gap: 14,
      "& p": {
        color: "#2F2F2F",
      },
      "& img": {
        marginRight: 5,
      },
    },
  },
  changePlan: {
    marginTop: 10,
    width: "70%",
    padding: "12px, 24px, 12px, 24px",
    color: "#09507F",
    lineHeight: "27px",
    borderRadius: 360,
    fontWeight: "bold",
    backgroundColor: "#EAEAEA",
    "&:hover": {
      backgroundColor: "#fcdeab",
    },
  },
  changePlan2: {
    marginTop: 10,
    width: "70%",
    padding: "12px, 24px, 12px, 24px",
    color: "#09507F",
    lineHeight: "27px",
    borderRadius: 360,
    fontWeight: "bold",
    backgroundColor: "#FFD27D",
    whiteSpace: "nowrap",
    marginLeft: "3%",
    "&:hover": {
      backgroundColor: "#fcdeab",
    },
  },
  flexBox: {
    display: "flex",
    alignItems: "center",
  },
  button1: {
    transition: "all .1s ease-in-out",
    border: "3px solid #fff",
    "&:hover": {
      // transform: "scale(1.05, 1.05)",
      animation: "$scale .2s ease-in-out",
      background: "transparent",
    },
  },
  button2: {
    transition: "all .1s ease-in-out",
    border: "3px solid #FFD27D",
    "&:hover": {
      // transform: "scale(1.05, 1.05)",
      animation: "$scale .2s ease-in-out",
      background: "transparent",
    },
  },
  "@keyframes scale": {
    "0%": {
      // transform: "scale(1.05, 1.05)",
      animation: "$scale .2s ease-in-out",
    },
    "50%": {
      transform: "scale(1.03, 1.03)",
    },
    "100%": {
      transform: "scale(1)",
    },
  },
  button: {
    transition: "all .1s ease-in-out",
    border: "3px solid #009A88",
    "&:hover": {
      background: "transparent",
      animation: "$scale .2s ease-in-out",
      color: "#009A88",
    },
  },
}));
